import { type LoaderData } from '../../routes/_marketing+/index'
import { motion } from 'framer-motion'
import { useState, useEffect, useRef } from 'react'
import * as THREE from 'three'

interface DynamicShowcaseProps {
	cosmetics: LoaderData['featuredCosmetics']
}

const COLORS = [
	'#FF1493', // Deep Pink
	'#00FFFF', // Cyan
	'#FF4500', // Orange Red
	'#32CD32', // Lime Green
	'#FFD700', // Gold
	'#8A2BE2', // Blue Viole
]

export function DynamicShowcase({ cosmetics }: DynamicShowcaseProps) {
	const [currentCosmeticIndex, setCurrentCosmeticIndex] = useState(0)
	const [currentColor, setCurrentColor] = useState(COLORS[0])
	const containerRef = useRef<HTMLDivElement>(null)
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
	const sceneRef = useRef<THREE.Scene | null>(null)
	const cameraRef = useRef<THREE.PerspectiveCamera | null>(null)
	const icosahedronRef = useRef<THREE.Mesh | null>(null)
	const wireframeRef = useRef<THREE.LineSegments | null>(null)

	useEffect(() => {
		if (!canvasRef.current) return

		const scene = new THREE.Scene()
		const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000)
		const renderer = new THREE.WebGLRenderer({
			canvas: canvasRef.current,
			alpha: true,
			antialias: true,
		})

		renderer.setSize(800, 800)
		renderer.setClearColor(0xffffff, 0)
		camera.position.z = 9

		const geometry = new THREE.IcosahedronGeometry(4)
		const material = new THREE.MeshPhysicalMaterial({
			color: new THREE.Color(currentColor),
			transparent: true,
			opacity: 0.3,
			metalness: 0.2,
			roughness: 0.1,
			clearcoat: 1,
			clearcoatRoughness: 0.1,
			side: THREE.DoubleSide,
			depthWrite: false,
		})

		const icosahedron = new THREE.Mesh(geometry, material)
		scene.add(icosahedron)

		const edges = new THREE.EdgesGeometry(geometry)
		const lineMaterial = new THREE.LineBasicMaterial({
			color: 0xffffff,
			linewidth: 2,
			transparent: true,
			opacity: 0.7,
			depthTest: true,
			depthWrite: true,
		})
		const wireframe = new THREE.LineSegments(edges, lineMaterial)
		wireframe.renderOrder = 1
		scene.add(wireframe)

		const ambientLight = new THREE.AmbientLight(0xffffff, 0.5)
		scene.add(ambientLight)

		const pointLight1 = new THREE.PointLight(0xffffff, 1, 100)
		pointLight1.position.set(5, 5, 5)
		scene.add(pointLight1)

		const pointLight2 = new THREE.PointLight(0xffffff, 0.5, 100)
		pointLight2.position.set(-5, -5, -5)
		scene.add(pointLight2)

		const animate = () => {
			requestAnimationFrame(animate)
			if (icosahedron && wireframe) {
				icosahedron.rotation.x += 0.005
				icosahedron.rotation.y += 0.005
				wireframe.rotation.x += 0.005
				wireframe.rotation.y += 0.005
			}
			renderer.render(scene, camera)
		}
		animate()

		rendererRef.current = renderer
		sceneRef.current = scene
		cameraRef.current = camera
		icosahedronRef.current = icosahedron
		wireframeRef.current = wireframe

		return () => {
			renderer.dispose()
			geometry.dispose()
			material.dispose()
			edges.dispose()
			lineMaterial.dispose()
		}
	}, [currentColor])

	useEffect(() => {
		if (cosmetics.length === 0) return

		const interval = setInterval(() => {
			setCurrentCosmeticIndex(prevIndex => (prevIndex + 1) % cosmetics.length)
			setCurrentColor(COLORS[Math.floor(Math.random() * COLORS.length)])
		}, 2000)

		return () => clearInterval(interval)
	}, [cosmetics.length])

	const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
		if (
			!icosahedronRef.current ||
			!wireframeRef.current ||
			!containerRef.current
		)
			return

		const { clientX, clientY } = event
		const { left, top, width, height } =
			containerRef.current.getBoundingClientRect()
		const x = ((clientX - left) / width) * 2 - 1
		const y = -((clientY - top) / height) * 2 + 1

		icosahedronRef.current.rotation.y = (x * Math.PI) / 2
		icosahedronRef.current.rotation.x = (y * Math.PI) / 2
		wireframeRef.current.rotation.y = (x * Math.PI) / 2
		wireframeRef.current.rotation.x = (y * Math.PI) / 2
	}

	if (cosmetics.length === 0) {
		return <div>No cosmetics available</div>
	}

	const currentCosmetic = cosmetics[currentCosmeticIndex]

	return (
		<section
			className="relative h-[900px] overflow-hidden bg-white py-16"
			ref={containerRef}
			onMouseMove={handleMouseMove}
		>
			<div className="container mx-auto">
				<div className="flex items-center justify-center">
					<div className="relative h-[800px] w-[800px]">
						<canvas ref={canvasRef} className="absolute inset-0 z-10" />
						<motion.div
							className="absolute inset-0 z-0 flex items-center justify-center"
							key={currentCosmeticIndex}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.3 }}
						>
							<div
								className="relative"
								style={{
									width: '70%',
									height: '70%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<img
									src={
										currentCosmetic.images?.smallIcon ||
										'/placeholder-outfit.jpg'
									}
									alt={currentCosmetic.name}
									className="max-h-full max-w-full object-contain"
									style={{
										filter: `drop-shadow(0 0 10px ${currentColor}) drop-shadow(0 0 20px ${currentColor}) brightness(1.2) contrast(1.1)`,
									}}
								/>
							</div>
						</motion.div>
					</div>
				</div>
			</div>
		</section>
	)
}
